import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  FileText,
  Sparkles,
  Download,
  Copy,
  Image as ImageIcon,
  Clock,
  Users,
  AlertTriangle,
  Target,
  FileCheck,
  MessageSquare,
  ClipboardList,
  Shield,
  TrendingUp,
  Loader2,
  Check,
  Trash2,
  Eye,
  BookOpen,
  Briefcase,
  Upload,
  Paperclip,
  CalendarClock,
  X
} from "lucide-react";
import { formatAIResponse } from "@/utils/formatAIContent";

// Template icons mapping
const templateIcons = {
  one_pager: Briefcase,
  staff_memo: MessageSquare,
  policy_brief: FileCheck,
  improvement_initiative: TrendingUp,
  a3_report: ClipboardList,
  sbar: Shield,
  root_cause_summary: AlertTriangle,
  training_material: BookOpen,
  safety_alert: AlertTriangle,
  change_management: Users,
  huddle_talking_points: MessageSquare,
  project_charter: Target,
  meeting_agenda: CalendarClock
};

// Template color classes
const templateColors = {
  one_pager: "bg-blue-500/10 border-blue-500/30 text-blue-400",
  staff_memo: "bg-violet-500/10 border-violet-500/30 text-violet-400",
  policy_brief: "bg-amber-500/10 border-amber-500/30 text-amber-400",
  improvement_initiative: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
  a3_report: "bg-cyan-500/10 border-cyan-500/30 text-cyan-400",
  sbar: "bg-red-500/10 border-red-500/30 text-red-400",
  root_cause_summary: "bg-orange-500/10 border-orange-500/30 text-orange-400",
  training_material: "bg-indigo-500/10 border-indigo-500/30 text-indigo-400",
  safety_alert: "bg-red-500/10 border-red-500/30 text-red-400",
  change_management: "bg-purple-500/10 border-purple-500/30 text-purple-400",
  huddle_talking_points: "bg-teal-500/10 border-teal-500/30 text-teal-400",
  project_charter: "bg-pink-500/10 border-pink-500/30 text-pink-400",
  meeting_agenda: "bg-sky-500/10 border-sky-500/30 text-sky-400"
};

export default function CommunicationStudio() {
  const [templates, setTemplates] = useState([]);
  const [imageTypes, setImageTypes] = useState([]);
  const [tones, setTones] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [viewDocument, setViewDocument] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    template_type: "",
    title: "",
    context: "",
    audience: "staff",
    tone_override: "",
    include_image: false,
    image_type: ""
  });
  
  // File attachment state
  const [attachedFile, setAttachedFile] = useState(null);
  const [fileContent, setFileContent] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, docsRes] = await Promise.all([
        axios.get(`${API}/communication/templates`, { withCredentials: true }),
        axios.get(`${API}/communication/documents`, { withCredentials: true })
      ]);
      
      setTemplates(templatesRes.data.templates || []);
      setImageTypes(templatesRes.data.image_types || []);
      setTones(templatesRes.data.tones || []);
      setDocuments(docsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFormData({
      ...formData,
      template_type: template.id,
      tone_override: template.default_tone
    });
  };

  // Handle file attachment for AI context
  const handleFileAttach = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type (PDF, Word, TXT)
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'text/markdown'
    ];
    
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error("Please attach a PDF, Word, or text file");
      return;
    }
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be under 5MB");
      return;
    }
    
    setUploadingFile(true);
    setAttachedFile(file);
    
    try {
      // Read file content
      if (file.type === 'text/plain' || file.type === 'text/markdown' || fileExtension === '.txt' || fileExtension === '.md') {
        const text = await file.text();
        setFileContent(text.substring(0, 10000)); // Limit to 10k chars
        toast.success(`Attached: ${file.name}`);
      } else {
        // For PDF/Word, we'll send to backend for extraction
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(`${API}/files/extract-text`, formDataUpload, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.text) {
          setFileContent(response.data.text.substring(0, 10000));
          toast.success(`Attached: ${file.name}`);
        } else {
          toast.error("Could not extract text from file");
          setAttachedFile(null);
        }
      }
    } catch (error) {
      console.error("Error processing file:", error);
      toast.error("Failed to process file. Try a text file instead.");
      setAttachedFile(null);
    } finally {
      setUploadingFile(false);
    }
  };
  
  const removeAttachedFile = () => {
    setAttachedFile(null);
    setFileContent("");
  };

  const handleGenerate = async () => {
    if (!formData.template_type || !formData.title || !formData.context) {
      toast.error("Please fill in all required fields");
      return;
    }

    setGenerating(true);
    try {
      // Build context with file content if attached
      let enhancedContext = formData.context;
      if (fileContent) {
        enhancedContext = `${formData.context}\n\n--- ATTACHED DOCUMENT CONTENT ---\n${fileContent}`;
      }
      
      const requestData = {
        ...formData,
        context: enhancedContext
      };
      
      const response = await axios.post(`${API}/communication/generate`, requestData, {
        withCredentials: true
      });
      
      toast.success("Document generated successfully!");
      setViewDocument(response.data);
      setShowPreview(true);
      fetchData(); // Refresh documents list
      
      // Reset form
      setFormData({
        template_type: "",
        title: "",
        context: "",
        audience: "staff",
        tone_override: "",
        include_image: false,
        image_type: ""
      });
      setSelectedTemplate(null);
      setAttachedFile(null);
      setFileContent("");
    } catch (error) {
      console.error("Error generating document:", error);
      toast.error(error.response?.data?.detail || "Failed to generate document");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard! Ready for SharePoint/Word");
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/communication/documents/${docId}`, {
        withCredentials: true
      });
      toast.success("Document deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handleViewDocument = async (docId) => {
    try {
      const response = await axios.get(`${API}/communication/documents/${docId}`, {
        withCredentials: true
      });
      setViewDocument(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error("Failed to load document");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="communication-studio" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            Communication Studio
          </h1>
          <p className="text-muted-foreground">
            AI-powered professional documents using Johns Hopkins frameworks
          </p>
        </div>
      </div>

      <Tabs defaultValue="create" className="space-y-6">
        <TabsList>
          <TabsTrigger value="create" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Create Document
          </TabsTrigger>
          <TabsTrigger value="library" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Document Library ({documents.length})
          </TabsTrigger>
        </TabsList>

        {/* CREATE TAB */}
        <TabsContent value="create" className="space-y-6">
          {/* Template Selection */}
          <Card className="bento-card">
            <CardHeader>
              <CardTitle className="text-lg">1. Choose Document Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {templates.map((template) => {
                  const IconComponent = templateIcons[template.id] || FileText;
                  const colorClass = templateColors[template.id] || "bg-zinc-500/10 border-zinc-500/30";
                  const isSelected = selectedTemplate?.id === template.id;
                  
                  return (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        isSelected
                          ? `${colorClass} ring-2 ring-primary`
                          : "bg-zinc-900/50 border-zinc-800 hover:border-zinc-700"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <IconComponent className={`w-5 h-5 ${isSelected ? '' : 'text-muted-foreground'}`} />
                        <span className="font-medium text-sm">{template.name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {template.description}
                      </p>
                      <Badge variant="outline" className="mt-2 text-xs">
                        {template.framework}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Document Details */}
          {selectedTemplate && (
            <Card className="bento-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  2. Document Details
                  <Badge variant="outline">{selectedTemplate.name}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Title */}
                <div className="space-y-2">
                  <Label>Document Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Fall Prevention Initiative Q1 2026"
                    data-testid="doc-title"
                  />
                </div>

                {/* Context */}
                <div className="space-y-2">
                  <Label>Context & Background *</Label>
                  <Textarea
                    value={formData.context}
                    onChange={(e) => setFormData({ ...formData, context: e.target.value })}
                    placeholder="Describe what you want to communicate. Include key points, data, goals, and any specific information the AI should incorporate..."
                    className="min-h-[150px]"
                    data-testid="doc-context"
                  />
                  <p className="text-xs text-muted-foreground">
                    The more detail you provide, the better the output. Include metrics, timelines, and specific goals.
                  </p>
                </div>

                {/* File Attachment */}
                <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-3">
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-cyan-400" />
                    <Label>Attach Document for AI Context (Optional)</Label>
                  </div>
                  
                  {attachedFile ? (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-cyan-400" />
                        <span className="text-sm text-cyan-300">{attachedFile.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {(attachedFile.size / 1024).toFixed(1)} KB
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={removeAttachedFile}
                        className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-cyan-500/50 hover:bg-cyan-500/5 transition-colors">
                        <input
                          type="file"
                          onChange={handleFileAttach}
                          accept=".pdf,.doc,.docx,.txt,.md"
                          className="hidden"
                          data-testid="file-upload"
                        />
                        {uploadingFile ? (
                          <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
                        ) : (
                          <Upload className="w-5 h-5 text-muted-foreground" />
                        )}
                        <span className="text-sm text-muted-foreground">
                          {uploadingFile ? "Processing..." : "Click to attach PDF, Word, or text file"}
                        </span>
                      </label>
                      <p className="text-xs text-muted-foreground text-center">
                        AI will use the document content to generate more accurate and relevant output
                      </p>
                    </div>
                  )}
                </div>

                {/* Audience & Tone */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Target Audience</Label>
                    <Select
                      value={formData.audience}
                      onValueChange={(value) => setFormData({ ...formData, audience: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="staff">All Staff</SelectItem>
                        <SelectItem value="leadership">Leadership Team</SelectItem>
                        <SelectItem value="clinical">Clinical Staff</SelectItem>
                        <SelectItem value="nursing">Nursing Staff</SelectItem>
                        <SelectItem value="all_hands">All Hands / Organization-wide</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Tone</Label>
                    <Select
                      value={formData.tone_override}
                      onValueChange={(value) => setFormData({ ...formData, tone_override: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {tones.map((tone) => (
                          <SelectItem key={tone} value={tone}>
                            {tone.charAt(0).toUpperCase() + tone.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Image Generation */}
                <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-violet-400" />
                      <Label>Generate Professional Visual</Label>
                    </div>
                    <Switch
                      checked={formData.include_image}
                      onCheckedChange={(checked) => setFormData({ ...formData, include_image: checked })}
                    />
                  </div>
                  
                  {formData.include_image && (
                    <div className="space-y-2">
                      <Label className="text-sm">Image Type</Label>
                      <Select
                        value={formData.image_type}
                        onValueChange={(value) => setFormData({ ...formData, image_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select image type..." />
                        </SelectTrigger>
                        <SelectContent>
                          {imageTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name} - {type.description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        AI will generate a corporate healthcare-style visual using Nano Banana
                      </p>
                    </div>
                  )}
                </div>

                {/* Sections Preview */}
                <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                  <Label className="text-sm mb-2 block">Document Sections</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate.sections.map((section, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {section}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Generate Button */}
                <Button
                  onClick={handleGenerate}
                  disabled={generating || !formData.title || !formData.context}
                  className="w-full capture-btn"
                  size="lg"
                  data-testid="generate-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating with AI...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Professional Document
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* LIBRARY TAB */}
        <TabsContent value="library">
          <Card className="bento-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Generated Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No documents generated yet.</p>
                  <p className="text-sm">Create your first professional document above!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => {
                    const IconComponent = templateIcons[doc.template_type] || FileText;
                    const colorClass = templateColors[doc.template_type] || "bg-zinc-500/10";
                    
                    return (
                      <div
                        key={doc.document_id}
                        className="flex items-center justify-between p-4 rounded-lg border border-zinc-800 bg-zinc-900/30 hover:bg-zinc-900/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${colorClass}`}>
                            <IconComponent className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium">{doc.title}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Badge variant="outline" className="text-xs">
                                {doc.framework_used}
                              </Badge>
                              <span>•</span>
                              <Clock className="w-3 h-3" />
                              <span>
                                {new Date(doc.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDocument(doc.document_id)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDocument(doc.document_id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Document Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {viewDocument?.title}
            </DialogTitle>
          </DialogHeader>
          
          {viewDocument && (
            <div className="space-y-4">
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={() => handleCopyToClipboard(viewDocument.formatted_text)}
                  variant="outline"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy for SharePoint
                </Button>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Badge variant="outline" className="ml-auto">
                  {viewDocument.framework_used}
                </Badge>
              </div>

              {/* Image Preview */}
              {viewDocument.image_base64 && (
                <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                  <Label className="text-sm mb-2 block">Generated Visual</Label>
                  <img
                    src={`data:${viewDocument.image_mime_type || 'image/png'};base64,${viewDocument.image_base64}`}
                    alt="Generated visual"
                    className="max-w-full h-auto rounded-lg"
                  />
                </div>
              )}

              {/* Document Content */}
              <ScrollArea className="h-[400px] rounded-lg border border-zinc-800 bg-zinc-950 p-4">
                <div className="prose prose-invert prose-sm max-w-none">
                  {viewDocument.sections && Object.entries(viewDocument.sections).map(([section, content]) => (
                    <div key={section} className="mb-6">
                      <h3 className="text-primary font-semibold border-b border-zinc-800 pb-2 mb-3">
                        {section}
                      </h3>
                      <div className="whitespace-pre-wrap text-sm text-zinc-300">
                        {formatAIResponse(content)}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
